import { Check, ChevronsUpDown, Globe, MapPin, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { countries } from '@/data/countries'
import { cn } from '@/lib/utils'

import type { RuntimeStepConfig } from '../types'

interface AddressFormStepConfigProps {
  value: RuntimeStepConfig['addressSettings']
  onChange: (config: RuntimeStepConfig['addressSettings']) => void
}

interface CountryOption {
  code: string
  name: string
}

const ALL_COUNTRIES: CountryOption[] = (countries as Array<Record<string, string>>)
  .map((c) => ({ code: c['alpha-2'], name: c.name }))
  .filter((c): c is CountryOption => !!c.code && !!c.name)
  .sort((a, b) => a.name.localeCompare(b.name))

const codeToName = new Map(ALL_COUNTRIES.map((c) => [c.code, c.name]))

// ISO-3166-1 alpha-2 → regional-indicator emoji flag (e.g. "FR" → 🇫🇷)
const countryFlag = (code: string): string =>
  code.length === 2
    ? String.fromCodePoint(...[...code.toUpperCase()].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65))
    : ''

export function AddressFormStepConfig({ value, onChange }: AddressFormStepConfigProps) {
  const { t } = useTranslation()

  const allowlist = value?.countryAllowlist
  const forcedCountry = value?.forcedCountry

  const updateAllowlist = (next: string[] | undefined) => {
    onChange({
      ...value,
      countryAllowlist: next && next.length > 0 ? next : undefined,
    })
  }

  const updateForced = (next: string | undefined) => {
    onChange({
      ...value,
      forcedCountry: next || undefined,
    })
  }

  return (
    <div className="space-y-4">
      <CountryMultiSelect
        label={t('stepEditor.addressForm.countryAllowlist')}
        description={t('stepEditor.addressForm.countryAllowlistDescription')}
        value={allowlist ?? []}
        onChange={updateAllowlist}
      />

      <ForcedCountrySelect
        label={t('stepEditor.addressForm.forcedCountry')}
        description={t('stepEditor.addressForm.forcedCountryDescription')}
        value={forcedCountry}
        onChange={updateForced}
      />
    </div>
  )
}

export function CountryMultiSelect({
  label,
  description,
  value,
  onChange,
  emptyLabel,
}: {
  label: string
  description: string
  value: string[]
  onChange: (next: string[] | undefined) => void
  emptyLabel?: string
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const selectedSet = useMemo(() => new Set(value), [value])

  const toggle = (code: string) => {
    if (selectedSet.has(code)) {
      onChange(value.filter((c) => c !== code))
    } else {
      onChange([...value, code])
    }
  }

  const remove = (code: string) => {
    onChange(value.filter((c) => c !== code))
  }

  const clearAll = () => onChange(undefined)

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">
        <Globe className="mr-1.5 inline h-3.5 w-3.5 text-slate-400" />
        {label}
      </Label>
      <p className="text-xs text-slate-500">{description}</p>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="h-8 w-full justify-between text-xs font-normal"
          >
            {value.length === 0
              ? emptyLabel ?? t('stepEditor.addressForm.allCountries')
              : t('stepEditor.addressForm.countriesSelected', { count: value.length })}
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command>
            <CommandInput
              placeholder={t('stepEditor.addressForm.searchCountry')}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>
                {t('stepEditor.addressForm.noCountryFound')}
              </CommandEmpty>
              <CommandGroup>
                {ALL_COUNTRIES.map((c) => {
                  const checked = selectedSet.has(c.code)
                  return (
                    <CommandItem
                      key={c.code}
                      value={`${c.name} ${c.code}`}
                      onSelect={() => toggle(c.code)}
                      className="text-xs"
                    >
                      <Check
                        className={cn(
                          'mr-2 h-3.5 w-3.5',
                          checked ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="mr-1.5">{countryFlag(c.code)}</span>
                      <span className="flex-1">{c.name}</span>
                      <span className="text-slate-400">{c.code}</span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((code) => (
            <span
              key={code}
              className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] dark:bg-slate-800"
            >
              {countryFlag(code)} {codeToName.get(code) ?? code}
              <button
                type="button"
                onClick={() => remove(code)}
                className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                aria-label={`Remove ${code}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={clearAll}
            className="text-[11px] text-slate-500 hover:underline"
          >
            {t('stepEditor.addressForm.clearAll')}
          </button>
        </div>
      )}
    </div>
  )
}

function ForcedCountrySelect({
  label,
  description,
  value,
  onChange,
}: {
  label: string
  description: string
  value: string | undefined
  onChange: (next: string | undefined) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">
        <MapPin className="mr-1.5 inline h-3.5 w-3.5 text-slate-400" />
        {label}
      </Label>
      <p className="text-xs text-slate-500">{description}</p>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="h-8 w-full justify-between text-xs font-normal"
          >
            {value
              ? `${countryFlag(value)} ${codeToName.get(value) ?? value} (${value})`
              : t('stepEditor.addressForm.useGeolocation')}
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command>
            <CommandInput
              placeholder={t('stepEditor.addressForm.searchCountry')}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>
                {t('stepEditor.addressForm.noCountryFound')}
              </CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="__use_geolocation__"
                  onSelect={() => {
                    onChange(undefined)
                    setOpen(false)
                  }}
                  className="text-xs"
                >
                  <Check
                    className={cn(
                      'mr-2 h-3.5 w-3.5',
                      !value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="flex-1 italic text-slate-600">
                    {t('stepEditor.addressForm.useGeolocation')}
                  </span>
                </CommandItem>
                {ALL_COUNTRIES.map((c) => (
                  <CommandItem
                    key={c.code}
                    value={`${c.name} ${c.code}`}
                    onSelect={() => {
                      onChange(c.code)
                      setOpen(false)
                    }}
                    className="text-xs"
                  >
                    <Check
                      className={cn(
                        'mr-2 h-3.5 w-3.5',
                        value === c.code ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="mr-1.5">{countryFlag(c.code)}</span>
                    <span className="flex-1">{c.name}</span>
                    <span className="text-slate-400">{c.code}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
